import * as React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DrawerComponent from "../../components/DrawerComponent/DrawerComponent";
import AppBarComponent from "../../components/AppBarComponent/AppBarComponent";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

const getToken = () => {
  const encoded = localStorage.getItem("authToken");
  return encoded ? atob(encoded) : "";
};

const esCorreoValido = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

type MailConfig = {
  servicio_url: string;
  timeout_ms: number;
  correo_prueba_default: string | null;
};

type PruebaResult = {
  ok: boolean;
  destinatario?: string;
  subject?: string;
  servicio?: { ejecuto: boolean; mensaje: string };
  message?: string;
};

export default function PruebaCorreoPage() {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [config, setConfig] = React.useState<MailConfig | null>(null);
  const [to, setTo] = React.useState("");
  const [cc, setCc] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [loadingConfig, setLoadingConfig] = React.useState(true);
  const [resultado, setResultado] = React.useState<PruebaResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const baseUrl = getProyectosApiBaseUrl();

  React.useEffect(() => {
    const cargar = async () => {
      try {
        setLoadingConfig(true);
        const response = await fetch(`${baseUrl}/mail/config`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "No fue posible leer configuración.");
        setConfig(data);
        if (data.correo_prueba_default) setTo(data.correo_prueba_default);
      } catch (err: any) {
        setError(err.message || "Error al cargar configuración de correo.");
      } finally {
        setLoadingConfig(false);
      }
    };
    cargar();
  }, [baseUrl]);

  const enviarPrueba = async () => {
    const destino = to.trim();
    if (!destino) {
      setError("Ingrese un correo destino válido (ej. nombre@emcali.com.co).");
      return;
    }
    if (!esCorreoValido(destino)) {
      setError("El correo destino no tiene un formato válido.");
      return;
    }
    const ccList = cc
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const ccInvalidos = ccList.filter((c) => !esCorreoValido(c));
    if (ccInvalidos.length) {
      setError(`Correos CC no válidos: ${ccInvalidos.join(", ")}`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResultado(null);
      const payload: { to: string; cc?: string[] } = { to: destino };
      if (ccList.length) payload.cc = ccList;

      const response = await fetch(`${baseUrl}/mail/prueba`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        const msg = data?.message;
        const detalle = Array.isArray(msg)
          ? msg.join(". ")
          : typeof msg === "string"
            ? msg
            : JSON.stringify(data?.detalle || data?.servicio || data);
        throw new Error(detalle || "El servicio de correo no respondió correctamente.");
      }
      if (data.ok === false) {
        throw new Error(data.message || "Configure el destinatario de prueba.");
      }
      setResultado(data);
    } catch (err: any) {
      setError(err.message || "Error al enviar correo de prueba.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <CssBaseline />
      <AppBarComponent
        open={open}
        handleDrawerOpen={() => setOpen((p) => !p)}
        auth
        anchorEl={anchorEl}
        handleMenu={(e) => setAnchorEl(e.currentTarget)}
        handleClose={() => setAnchorEl(null)}
        title="Prueba de correo"
      />
      <DrawerComponent open={open} handleDrawerClose={() => setOpen(false)} />
      <Box component="main" sx={{ flexGrow: 1, overflow: "auto", pt: "116px", pb: "56px" }}>
        <Container maxWidth="sm" sx={{ mt: 1 }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
              Prueba de envío de correo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Valida la conexión con el servicio EMCALI (
              <code>communal_services/mail/send</code>) antes de integrar la confirmación al crear
              proyectos.
            </Typography>

            {loadingConfig ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Stack spacing={2}>
                {config && (
                  <Alert severity="info">
                    Servicio: {config.servicio_url} · Timeout: {config.timeout_ms} ms
                    {config.correo_prueba_default
                      ? ` · Destino por defecto: ${config.correo_prueba_default}`
                      : ""}
                  </Alert>
                )}
                {error && <Alert severity="error">{error}</Alert>}
                {resultado?.ok && (
                  <Alert severity="success">
                    Correo enviado a <strong>{resultado.destinatario}</strong>. Respuesta del
                    servicio: ejecuto={String(resultado.servicio?.ejecuto)}, mensaje=&quot;
                    {resultado.servicio?.mensaje}&quot;
                  </Alert>
                )}
                <TextField
                  label="Correo destino (to)"
                  type="email"
                  fullWidth
                  size="small"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  helperText="O configure EMCALI_MAIL_TEST_TO en el servidor"
                />
                <TextField
                  label="CC (opcional, separados por coma)"
                  fullWidth
                  size="small"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="opcional@emcali.com.co, otro@emcali.com.co"
                  helperText="Deje vacío si no necesita copia. Solo correos válidos."
                />
                <Button
                  variant="contained"
                  disabled={loading || !to.trim()}
                  onClick={enviarPrueba}
                >
                  {loading ? "Enviando…" : "Enviar correo de prueba"}
                </Button>
              </Stack>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
